import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { Box, Typography, CircularProgress, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Form, Button, Spacer } from 'src/components/shared';
import { registerSetPassword } from '../../actions/authActions';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { checkUserVerificationStatus } from 'src/modules/auth/api/authApis';

const StyledPasswordInput = styled(Form.Field.Input, {
  shouldForwardProp: (prop) => prop !== 'width',
})(({ theme, width = '100%' }) => ({
  marginTop: '0 !important',
  marginBottom: '17px !important',
  width: width,
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '8px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent !important',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main} !important`,
  },
  '& .MuiInputBase-input::placeholder': {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
  color: theme.palette.text.primary,
  '& .MuiFormHelperText-root': {
    color: theme.palette.error.main,
    backgroundColor: 'transparent',
    marginLeft: 10,
    marginTop: 10,
  },
}));

const SetPasswordForm = ({ router, isAuthenticated, ...props }) => {
  const [loading, setLoading] = React.useState(false);
  const [checkingVerification, setCheckingVerification] = React.useState(false);
  const [userDetails, setUserDetails] = React.useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    terms: false,
    updates: false,
    inviteRef: '',
  });
  const history = useHistory();

  useEffect(() => {
    if (
      router.location?.query?.uid &&
      router.location?.query?.uid !== '' &&
      isAuthenticated === false
    ) {
      setCheckingVerification(true);
      checkUserVerificationStatus(router.location?.query?.uid)
        .then((res) => {
          setUserDetails({
            email: res?.user?.email || '',
            firstName: res?.user?.fname || '',
            lastName: res?.user?.lname || '',
            inviteRef: res?.user?.inviteRef || '',
            password: '',
            terms: false,
            updates: false,
          });
          if (res?.verified && res?.verified === true) {
            history.replace('/auth/login');
          }
          setCheckingVerification(false);
        })
        .catch((error) => {
          console.error('Error: ', error);
          setCheckingVerification(false);
        });
    } else {
      setCheckingVerification(false);
    }
  }, [router.location?.query?.uid, isAuthenticated, history]);

  const userId = router.location?.query?.uid;

  if (checkingVerification) {
    return (
      <React.Fragment>
        <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
          <CircularProgress size={24} color="secondary" />
          <Spacer x={2} y={2} />
          <Typography component="h1" color="textSecondary">
            Please wait while we fetch your account details.
          </Typography>
        </Box>
      </React.Fragment>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={'31px'}
    >
      <Typography
        color="textPrimary"
        fontSize={57}
        fontWeight={100}
        align="center"
        width="100%"
        lineHeight={1}
        sx={{ letterSpacing: '2px' }}
      >
        You've been invited to join SuperReach!
      </Typography>
      <Typography variant="body1" align="center" color="textSecondary">
        Create Your User Account
      </Typography>
      <div style={{ width: 500, margin: '0 auto' }}>
        <Form
          initialValues={userDetails}
          enableReinitialize
          validationSchema={Yup.object().shape({
            password: Yup.string()
              .required('Please enter your password!')
              .min(8, 'Password must be at least 8 characters long!')
              .matches(
                /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])[A-Za-z\d!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{8,}$/,
                'Password must contain 8 Characters, One Uppercase, One Lowercase, One Number and one Special Character',
              ),
            firstName: Yup.string().required('First name is required'),
            lastName: Yup.string().required('Last name is required'),
            terms: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
            updates: Yup.boolean(),
          })}
          onSubmit={async (values, form) => {
            setLoading(true);
            const payload = {
              password: values.password,
              confirmPassword: values.password,
              privacyPolicyAccepted: values.terms ?? false,
              termsAccepted: values.terms ?? false,
              fname: values.firstName,
              lname: values.lastName,
              inviteRef: values.inviteRef,
            };
            await props.registerSetPassword(userId, payload);
            if (values.inviteRef === 'lms') {
              window.location.replace(process.env.REACT_APP_LMS_URL + '/lms-journey');
            } else {
              history.replace('/');
            }
            setLoading(false);
          }}
        >
          {(props) => {
            return (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  props.submitForm();
                  return false;
                }}
                noValidate
              >
                <StyledPasswordInput
                  fullWidth
                  variant="outlined"
                  type="email"
                  name="email"
                  placeholder="Email"
                  disabled={true}
                />

                <StyledPasswordInput
                  fullWidth
                  variant="outlined"
                  type="password"
                  name="password"
                  placeholder="Set a Password"
                  disabled={loading}
                  autoComplete="current-password"
                />

                <Box display="flex" gap={2}>
                  <StyledPasswordInput
                    variant="outlined"
                    type="text"
                    name="firstName"
                    placeholder="First name"
                    autoComplete="given-name"
                    sx={{ flexGrow: 1 }}
                    disabled={loading}
                  />

                  <StyledPasswordInput
                    variant="outlined"
                    type="text"
                    name="lastName"
                    placeholder="Last name"
                    autoComplete="family-name"
                    sx={{ flexGrow: 1 }}
                    disabled={loading}
                  />
                </Box>

                <Box>
                  <Form.Field.Checkbox
                    name="terms"
                    label={
                      <Typography>
                        I agree to SuperReach's{' '}
                        <Link
                          color="secondary"
                          target="_blank"
                          href="https://superreach.com/privacy-policy/"
                          sx={{ textDecoration: 'none' }}
                        >
                          Privacy Policy
                        </Link>{' '}
                        &{' '}
                        <Link
                          color="secondary"
                          target="_blank"
                          href="https://superreach.com/terms-of-service/"
                          sx={{ textDecoration: 'none' }}
                        >
                          Terms of Service
                        </Link>
                      </Typography>
                    }
                  />
                </Box>

                <Box>
                  <Form.Field.Checkbox
                    name="updates"
                    label={<Typography>Yes, keep me in the loop with updates and tips.</Typography>}
                  />
                </Box>

                <Box mt={'31px'}>
                  <Button
                    variant="contained"
                    color="secondary"
                    type="submit"
                    loading={loading}
                    fullWidth
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    disabled={loading || !props.dirty}
                  >
                    Next
                  </Button>
                </Box>

                <Spacer x={2} y={2} />
              </form>
            );
          }}
        </Form>
      </div>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  router: state.router,
  isAuthenticated: state.auth.isAuthenticated,
});

const mapDispatchToProps = (dispatch) => ({
  registerSetPassword: (uid, data) => {
    return new Promise((resolve, reject) => {
      dispatch(registerSetPassword(uid, { ...data, redirect: true }, resolve, reject));
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SetPasswordForm);
